import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  message,
  Tag,
  Modal,
  Form,
  InputNumber,
  Alert,
  Tooltip,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  WarningOutlined,
  EditOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { stockService } from '../../services';
import { formatNumber, formatDate, debounce } from '../../utils/helpers';

const { Option } = Select;

const StockList = () => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    stock_status: 'all', // all, low, out, normal
    category_id: 'all',
  });
  const [categories, setCategories] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [adjustForm] = Form.useForm();

  useEffect(() => {
    loadVariants();
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 📱 Mobile History Card Component
  const MobileHistoryCard = ({ record }) => {
    const typeMap = {
      'export': { color: 'red', text: 'Xuất kho' },
      'import': { color: 'green', text: 'Nhập kho' },
      'increase': { color: 'green', text: 'Tăng' },
      'decrease': { color: 'red', text: 'Giảm' },
      'sale': { color: 'blue', text: 'Bán hàng' },
      'adjustment': { color: 'orange', text: 'Điều chỉnh' },
    };
    const typeConfig = typeMap[record.change_type] || { color: 'default', text: record.change_type };
    
    return (
      <Card
        size="small"
        style={{
          marginBottom: 8,
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: 10 }}
      >
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 6
          }}>
            <div>
              <Tag color={typeConfig.color} size="small">
                {typeConfig.text}
              </Tag>
              <span style={{ 
                fontSize: 11, 
                color: '#666',
                marginLeft: 4
              }}>
                {formatDate(record.changed_at)}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 'bold',
                color: record.change_amount > 0 ? '#52c41a' : '#ff4d4f'
              }}>
                {record.change_amount > 0 ? '+' : ''}{formatNumber(record.change_amount)}
              </div>
              <div style={{ fontSize: 10, color: '#666' }}>
                → {formatNumber(record.stock_after)}
              </div>
            </div>
          </div>
          
          {record.note && (
            <div style={{ 
              fontSize: 11, 
              color: '#666',
              marginTop: 6,
              padding: 6,
              backgroundColor: '#f8f9fa',
              borderRadius: 4,
              lineHeight: 1.3
            }}>
              {record.note}
            </div>
          )}
          
          <div style={{ 
            fontSize: 10, 
            color: '#999',
            textAlign: 'right',
            marginTop: 4
          }}>
            User ID: {record.changed_by}
          </div>
        </div>
      </Card>
    );
  };

  // 📱 Mobile Stock Card Component
  const MobileStockCard = ({ variant }) => {
    const stockStatus = getStockStatus(variant);
    const stock = variant.stock || 0;
    const lowStockAlert = variant.low_stock_alert || 5;
    const shouldReorder = stock <= lowStockAlert;
    const suggestedOrder = shouldReorder ? (lowStockAlert * 2) - stock : 0;
    
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          border: `2px solid ${
            stockStatus.color === 'red' ? '#ff4d4f' : 
            stockStatus.color === 'orange' ? '#fa8c16' : '#52c41a'
          }`,
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: 12 }}
      >
        <div>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: 14, 
            marginBottom: 4,
            color: '#333'
          }}>
            {variant.product_name}
          </div>
          
          <div style={{ 
            fontSize: 11, 
            color: '#666',
            marginBottom: 6 
          }}>
            SKU: {variant.sku || 'N/A'}
          </div>

          <div style={{ marginBottom: 8 }}>
            {variant.category_name && (
              <Tag size="small" color="blue" style={{ marginRight: 4 }}>
                {variant.category_name}
              </Tag>
            )}
            <Tag 
              size="small" 
              color={stockStatus.color}
              style={{ marginRight: 4 }}
            >
              {stockStatus.text}
            </Tag>
            {shouldReorder && (
              <Tag size="small" color="red">
                Cần đặt hàng
              </Tag>
            )}
          </div>

          {variant.attributes && (
            <div style={{ 
              fontSize: 11, 
              color: '#666',
              marginBottom: 8,
              fontStyle: 'italic'
            }}>
              {variant.attributes}
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8
          }}>
            <div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 'bold',
                color: stockStatus.color === 'red' ? '#ff4d4f' : 
                       stockStatus.color === 'orange' ? '#fa8c16' : '#52c41a'
              }}>
                SL: {formatNumber(stock)}
                {stockStatus.status === 'low' && <WarningOutlined style={{ marginLeft: 4, color: '#fa8c16' }} />}
                {stockStatus.status === 'out' && <ExclamationCircleOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />}
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>
                Cảnh báo: {formatNumber(lowStockAlert)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 'bold',
                color: '#1890ff' 
              }}>
                {formatNumber(variant.price || 0)}đ
              </div>
              {shouldReorder && (
                <div style={{ fontSize: 10, color: '#ff4d4f' }}>
                  Đề xuất: {formatNumber(suggestedOrder)}
                </div>
              )}
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8
          }}>
            <Space size={4}>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleAdjustStock(variant)}
              >
                Điều chỉnh
              </Button>
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => handleViewHistory(variant)}
              >
                Lịch sử
              </Button>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  const loadVariants = async () => {
    setLoading(true);
    try {
      const response = await stockService.getVariants();
      if (response.success) {
        const variantData = response.data || [];
        setVariants(variantData);
        
        // Lấy danh sách categories từ dữ liệu
        const uniqueCategories = [...new Map(
          variantData.map(item => [item.category_id, { id: item.category_id, name: item.category_name }])
        ).values()];
        setCategories(uniqueCategories);
      } else {
        message.error(response.message || 'Không thể tải danh sách tồn kho');
      }
    } catch (error) {
      //console.error('Error loading variants:', error);
      message.error('Không thể tải danh sách tồn kho');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, 500);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAdjustStock = (variant) => {
    setCurrentVariant(variant);
    adjustForm.setFieldsValue({
      current_stock: variant.stock || 0,
      new_stock: variant.stock || 0,
      reason: '',
    });
    setAdjustModalVisible(true);
  };

  const handleViewHistory = async (variant) => {
    //console.log('handleViewHistory called with variant:', variant);
    //console.log('Current accessToken:', localStorage.getItem('accessToken'));
    setCurrentVariant(variant);
    try {
      //console.log('Calling stockService.getHistory with variant_id:', variant.variant_id);
      const response = await stockService.getHistory(variant.variant_id, {
        page: 1,
        pageSize: 20
      });
      //console.log('Stock history response:', response);
      if (response.success) {
        //console.log('Setting stock history data:', response.data);
        setStockHistory(response.data || []);
      } else {
        //console.log('API returned failure:', response.message);
        message.error(response.message || 'Không thể tải lịch sử tồn kho');
      }
    } catch (error) {
      //console.error('Error loading stock history:', error);
      //console.error('Error details:', error.response?.data);
      let errorMessage = 'Không thể tải lịch sử tồn kho';
      if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }
      message.error(errorMessage);
      setStockHistory([]);
    }
    setHistoryModalVisible(true);
  };

  const handleSaveStockAdjustment = async (values) => {
    try {
      //console.log('🔄 Bắt đầu điều chỉnh tồn kho...');
      //console.log('Current token:', localStorage.getItem('accessToken'));
      //console.log('Variant ID:', currentVariant.variant_id);
      //console.log('Form values:', values);
      
      const requestData = {
        newStock: values.new_stock,
        changeType: "manual",
        note: values.reason,
      };
      //console.log('Request data:', requestData);
      
      const response = await stockService.update(currentVariant.variant_id, requestData);
      //console.log('API Response:', response);

      if (response.success) {
        message.success('Điều chỉnh tồn kho thành công');
        setAdjustModalVisible(false);
        loadVariants();
      } else {
        message.error(response.message || 'Điều chỉnh tồn kho thất bại');
      }
    } catch (error) {
      //console.error('❌ Lỗi điều chỉnh tồn kho:', error);
      //console.error('Error response:', error.response);
      
      if (error.response?.status === 401) {
        message.error('Token hết hạn, vui lòng đăng nhập lại');
      } else {
        message.error(`Điều chỉnh tồn kho thất bại: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const getStockStatus = (variant) => {
    const stock = variant.stock || 0;
    const lowStockAlert = variant.low_stock_alert || 5;

    if (stock === 0) {
      return { status: 'out', color: 'red', text: 'Hết hàng' };
    } else if (stock <= lowStockAlert) {
      return { status: 'low', color: 'orange', text: 'Sắp hết' };
    } else {
      return { status: 'normal', color: 'green', text: 'Bình thường' };
    }
  };

  const getStockProgress = (variant) => {
    const stock = variant.stock || 0;
    const lowStockAlert = variant.low_stock_alert || 5;
    const maxStock = lowStockAlert * 4; // Giả định max stock = 4 lần mức cảnh báo
    const percentage = Math.min((stock / maxStock) * 100, 100);
    
    let status = 'normal';
    if (percentage < 20) status = 'exception';
    else if (percentage < 50) status = 'active';

    return { percentage, status };
  };

  // Lọc variants dựa trên filters
  const filteredVariants = variants.filter(variant => {
    // Lọc theo search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!variant.product_name?.toLowerCase().includes(searchLower) &&
          !variant.sku?.toLowerCase().includes(searchLower) &&
          !variant.attributes?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Lọc theo category
    if (filters.category_id !== 'all' && variant.category_id !== parseInt(filters.category_id)) {
      return false;
    }

    // Lọc theo trạng thái tồn kho
    if (filters.stock_status !== 'all') {
      const stockStatus = getStockStatus(variant);
      if (stockStatus.status !== filters.stock_status) {
        return false;
      }
    }

    return true;
  });

  const columns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      fixed: 'left',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{record.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: 2 }}>
            SKU: {record.sku || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.category_name && <Tag size="small">{record.category_name}</Tag>}
          </div>
          {record.attributes && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
              {record.attributes}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tồn kho hiện tại',
      dataIndex: 'stock',
      key: 'stock',
      width: 150,
      render: (stock, record) => {
        const stockStatus = getStockStatus(record);
        return (
          <Space direction="vertical" size="small">
            <Space>
              <span style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: stockStatus.color === 'red' ? '#ff4d4f' : 
                       stockStatus.color === 'orange' ? '#fa8c16' : '#52c41a'
              }}>
                {formatNumber(stock || 0)}
              </span>
              {stockStatus.status === 'low' && <WarningOutlined style={{ color: '#fa8c16' }} />}
              {stockStatus.status === 'out' && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
            </Space>
            <Tag color={stockStatus.color} size="small">{stockStatus.text}</Tag>
          </Space>
        );
      },
      sorter: (a, b) => (a.stock || 0) - (b.stock || 0),
    },
    {
      title: 'Cảnh báo tồn kho',
      dataIndex: 'low_stock_alert',
      key: 'low_stock_alert',
      width: 120,
      render: (lowStockAlert, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ fontSize: '12px' }}>
            Mức cảnh báo: <strong>{formatNumber(lowStockAlert || 5)}</strong>
          </div>
          <Progress
            {...getStockProgress(record)}
            size="small"
            showInfo={false}
            style={{ width: 80 }}
          />
        </Space>
      ),
    },
    {
      title: 'Giá bán',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price) => (
        <div style={{ color: '#1890ff', fontWeight: 'bold' }}>
          {formatNumber(price || 0)} đ
        </div>
      ),
      sorter: (a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0),
    },
    {
      title: 'Cần đặt hàng',
      key: 'need_reorder',
      width: 120,
      render: (_, record) => {
        const stock = record.stock || 0;
        const lowStockAlert = record.low_stock_alert || 5;
        const shouldReorder = stock <= lowStockAlert;
        const suggestedOrder = shouldReorder ? (lowStockAlert * 2) - stock : 0;
        
        return shouldReorder ? (
          <Space direction="vertical" size="small">
            <Tag color="red" size="small">Cần đặt hàng</Tag>
            <div style={{ fontSize: '12px' }}>
              Đề xuất: {formatNumber(suggestedOrder)}
            </div>
          </Space>
        ) : (
          <Tag color="green" size="small">Đủ hàng</Tag>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Điều chỉnh tồn kho">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleAdjustStock(record)}
            />
          </Tooltip>
          <Tooltip title="Lịch sử tồn kho">
            <Button
              icon={<HistoryOutlined />}
              size="small"
              onClick={() => handleViewHistory(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Ngày',
      dataIndex: 'changed_at',
      key: 'changed_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Loại thay đổi',
      dataIndex: 'change_type',
      key: 'change_type',
      render: (type) => {
        const typeMap = {
          'export': { color: 'red', text: 'Xuất kho' },
          'import': { color: 'green', text: 'Nhập kho' },
          'increase': { color: 'green', text: 'Tăng' },
          'decrease': { color: 'red', text: 'Giảm' },
          'sale': { color: 'blue', text: 'Bán hàng' },
          'adjustment': { color: 'orange', text: 'Điều chỉnh' },
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Số lượng thay đổi',
      dataIndex: 'change_amount',
      key: 'change_amount',
      render: (change) => (
        <span style={{ color: change > 0 ? '#52c41a' : '#ff4d4f' }}>
          {change > 0 ? '+' : ''}{formatNumber(change)}
        </span>
      ),
    },
    {
      title: 'Tồn kho sau thay đổi',
      dataIndex: 'stock_after',
      key: 'stock_after',
      render: (stock) => formatNumber(stock),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (note) => (
        <Tooltip placement="topLeft" title={note}>
          <span>{note}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Người thực hiện',
      dataIndex: 'changed_by',
      key: 'changed_by',
      render: (userId) => `User ID: ${userId}`,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* Cảnh báo tồn kho thấp */}
        {filteredVariants.filter(v => getStockStatus(v).status === 'low' || getStockStatus(v).status === 'out').length > 0 && (
          <Alert
            message={`Cảnh báo: Có ${filteredVariants.filter(v => getStockStatus(v).status === 'low').length} sản phẩm sắp hết hàng và ${filteredVariants.filter(v => getStockStatus(v).status === 'out').length} sản phẩm đã hết hàng`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Tìm kiếm sản phẩm, SKU, thuộc tính..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Trạng thái tồn kho"
              value={filters.stock_status}
              onChange={(value) => handleFilterChange('stock_status', value)}
              style={{ width: '100%' }}
            >
              <Option value="all">Tất cả trạng thái</Option>
              <Option value="normal">Bình thường</Option>
              <Option value="low">Sắp hết</Option>
              <Option value="out">Hết hàng</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Danh mục"
              value={filters.category_id}
              onChange={(value) => handleFilterChange('category_id', value)}
              style={{ width: '100%' }}
            >
              <Option value="all">Tất cả danh mục</Option>
              {categories.map(category => (
                <Option key={category.id} value={category.id.toString()}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadVariants}
                loading={loading}
              />
            </Space>
          </Col>
        </Row>

        {/* Thống kê tổng quan */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={6}>
            <div style={{ background: '#f0f9ff', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {filteredVariants.length}
              </div>
              <div style={{ color: '#666' }}>Tổng variants</div>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                {filteredVariants.filter(v => getStockStatus(v).status === 'normal').length}
              </div>
              <div style={{ color: '#666' }}>Tồn kho tốt</div>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div style={{ background: '#fff7e6', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                {filteredVariants.filter(v => getStockStatus(v).status === 'low').length}
              </div>
              <div style={{ color: '#666' }}>Sắp hết</div>
            </div>
          </Col>
          <Col xs={24} sm={6}>
            <div style={{ background: '#fff2f0', padding: 16, borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff4d4f' }}>
                {filteredVariants.filter(v => getStockStatus(v).status === 'out').length}
              </div>
              <div style={{ color: '#666' }}>Hết hàng</div>
            </div>
          </Col>
        </Row>

        {/* Responsive Stock Display */}
        {isMobile ? (
          // Mobile: Card Layout
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Hiển thị {filteredVariants.length} variants
              </div>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadVariants}
                loading={loading}
              >
                Tải lại
              </Button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Đang tải...</div>
              </div>
            ) : filteredVariants.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
                <div>Không có tồn kho nào</div>
              </div>
            ) : (
              filteredVariants.map(variant => (
                <MobileStockCard 
                  key={variant.variant_id} 
                  variant={variant} 
                />
              ))
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredVariants}
            rowKey="variant_id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} variants`,
            }}
            scroll={{ x: 1400 }}
          />
        )}
      </Card>

      {/* Modal điều chỉnh tồn kho */}
      <Modal
        title="Điều chỉnh tồn kho"
        visible={adjustModalVisible}
        onCancel={() => setAdjustModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={adjustForm}
          layout="vertical"
          onFinish={handleSaveStockAdjustment}
        >
          <Alert
            message={
              <div>
                <div><strong>Sản phẩm:</strong> {currentVariant?.product_name}</div>
                <div><strong>SKU:</strong> {currentVariant?.sku}</div>
                {currentVariant?.attributes && (
                  <div><strong>Thuộc tính:</strong> {currentVariant.attributes}</div>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item name="current_stock" label="Tồn kho hiện tại">
                <InputNumber style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="new_stock"
                label="Tồn kho mới"
                rules={[
                  { required: true, message: 'Vui lòng nhập tồn kho mới' },
                  { type: 'number', min: 0, message: 'Tồn kho phải lớn hơn hoặc bằng 0' },
                ]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="reason"
                label="Ghi chú điều chỉnh"
                rules={[{ required: true, message: 'Vui lòng nhập ghi chú điều chỉnh' }]}
              >
                <Input.TextArea rows={3} placeholder="Nhập ghi chú điều chỉnh tồn kho" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setAdjustModalVisible(false)}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                Lưu thay đổi
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal lịch sử tồn kho */}
      <Modal
        title={
          <div>
            Lịch sử tồn kho - {currentVariant?.product_name}
            <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              SKU: {currentVariant?.sku} {currentVariant?.attributes && `| ${currentVariant.attributes}`}
            </div>
          </div>
        }
        visible={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={isMobile ? '95%' : 1000}
        style={isMobile ? { top: 20 } : {}}
      >
        {isMobile ? (
          // Mobile: Card Layout
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {stockHistory.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
                <div>Không có lịch sử nào</div>
              </div>
            ) : (
              <>
                <div style={{ 
                  marginBottom: 12,
                  padding: '8px 12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#1890ff'
                }}>
                  📱 Hiển thị {stockHistory.length} bản ghi lịch sử
                </div>
                {stockHistory.map((record, index) => (
                  <MobileHistoryCard key={record.id || index} record={record} />
                ))}
              </>
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            dataSource={stockHistory}
            columns={historyColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 900 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default StockList;
